import React from "react";

import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import qs from "query-string";
import { useSelector } from "react-redux";
import { Redirect, useLocation, useParams } from "react-router-dom";

import { TaskDetailsV3 } from "@fartherfinance/frontend/api/Tasks/Types";
import { ClientId, PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import CreateEquivalentSecurities from "@src/multiCustodian/components/Admin/EquivalentSecurities/CreateEquivalentSecurities";
import DeleteEquivalentSecurities from "@src/multiCustodian/components/Admin/EquivalentSecurities/DeleteEquivalentSecurities";
import EquivalentSecuritiesDrawer from "@src/multiCustodian/components/Admin/EquivalentSecurities/Drawer/EquivalentSecuritiesDrawer";
import EditEquivalentSecurities from "@src/multiCustodian/components/Admin/EquivalentSecurities/EditEquivalentSecurities";
import TaxBudgetDetails from "@src/multiCustodian/components/Advisor/ClientProfile/Components/TaxBudgetDetails";
import CreateClient from "@src/multiCustodian/components/Advisor/ClientProfile/Forms/CreateClient";
import HouseholdsDrawerRouter from "@src/multiCustodian/components/Advisor/Households/HouseholdsDrawerRouter";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Admin from "@src/multiCustodian/pages/Advisor/Admin";
import AllPortfoliosBilling from "@src/multiCustodian/pages/Advisor/Admin/AllPortfoliosBilling";
import CreateAdvisor from "@src/multiCustodian/pages/Advisor/Admin/CreateAdvisor";
import CreateBulkClients from "@src/multiCustodian/pages/Advisor/Admin/CreateBulkClients";
import CreateCXMember from "@src/multiCustodian/pages/Advisor/Admin/CreateCXMember";
import CustomModelBilling from "@src/multiCustodian/pages/Advisor/Admin/CustomModelBilling";
import DisclosureAgreements from "@src/multiCustodian/pages/Advisor/Admin/DisclosureAgreements";
import FAMBilling from "@src/multiCustodian/pages/Advisor/Admin/FAMBilling";
import BookAnalytics from "@src/multiCustodian/pages/Advisor/BookAnalytics/BookAnalyticsWrapper";
import Clients from "@src/multiCustodian/pages/Advisor/Clients";
import AdminArchiveAccount from "@src/multiCustodian/pages/Advisor/Clients/AdminArchiveAccount";
import AdminDeleteAccount from "@src/multiCustodian/pages/Advisor/Clients/AdminDeleteAccount";
import AdminRenameAccount from "@src/multiCustodian/pages/Advisor/Clients/AdminRenameAccount";
import AdvisorClientActions from "@src/multiCustodian/pages/Advisor/Clients/AdvisorClientActions";
import AdvisorClientAdmin from "@src/multiCustodian/pages/Advisor/Clients/AdvisorClientAdmin";
import AdvisorClientNotes from "@src/multiCustodian/pages/Advisor/Clients/AdvisorClientNotes";
import AdvisorClientTaxBudget from "@src/multiCustodian/pages/Advisor/Clients/AdvisorClientTaxBudget";
import AdvisorCopilot from "@src/multiCustodian/pages/Advisor/Clients/AdvisorCopilot";
import ClientProfile from "@src/multiCustodian/pages/Advisor/Clients/ClientProfile";
import ItbPacks from "@src/multiCustodian/pages/Advisor/FinancialOperations/ItbPacks";
import Itbs from "@src/multiCustodian/pages/Advisor/FinancialOperations/itbs";
import Rebalances from "@src/multiCustodian/pages/Advisor/FinancialOperations/Rebalances";
import StagedOrders from "@src/multiCustodian/pages/Advisor/FinancialOperations/StagedOrders";
import TradeProposal from "@src/multiCustodian/pages/Advisor/FinancialOperations/TradeProposal";
import TradeProposalBundle from "@src/multiCustodian/pages/Advisor/FinancialOperations/TradeProposalBundle";
import TradeProposalBundleProposalDetail from "@src/multiCustodian/pages/Advisor/FinancialOperations/TradeProposalBundleProposalDetail";
import TradeProposalBundles from "@src/multiCustodian/pages/Advisor/FinancialOperations/TradeProposalBundles";
import TradeProposals from "@src/multiCustodian/pages/Advisor/FinancialOperations/TradeProposals";
import InsuranceMarketplace from "@src/multiCustodian/pages/Advisor/InsuranceMarketplace";
import Investments from "@src/multiCustodian/pages/Advisor/Investments";
import AltAssetsMarketplace from "@src/multiCustodian/pages/Advisor/Investments/AltAssetsMarketplace";
import AltAssetsOfferingDetails from "@src/multiCustodian/pages/Advisor/Investments/AltAssetsMarketplace/AltAssetsOfferingDetailsAdvisor";
import AltAssetsOfferingsList from "@src/multiCustodian/pages/Advisor/Investments/AltAssetsMarketplace/AltAssetsOfferingsListAdvisor";
import AccessTab from "@src/multiCustodian/pages/Advisor/Investments/ModelMarketplaceV2/:portfolioPlanId/Access";
import ModelDrawerV2 from "@src/multiCustodian/pages/Advisor/Investments/ModelMarketplaceV2/:portfolioPlanId/ModelDetails";
import PortfolioLabUpdate from "@src/multiCustodian/pages/Advisor/Investments/ModelMarketplaceV2/:portfolioPlanId/Update";
import ModelDrawerUsageV2 from "@src/multiCustodian/pages/Advisor/Investments/ModelMarketplaceV2/:portfolioPlanId/Usage";
import PortfolioLabCreate from "@src/multiCustodian/pages/Advisor/Investments/ModelMarketplaceV2/Create";
import PortfolioPlanList from "@src/multiCustodian/pages/Advisor/Investments/ModelMarketplaceV2/Index";
import SMA from "@src/multiCustodian/pages/Advisor/Investments/SMA";
import SMADetails from "@src/multiCustodian/pages/Advisor/Investments/SMA/:SMAId";
import ApplySMA from "@src/multiCustodian/pages/Advisor/Investments/SMA/:SMAId/Apply";
import LendingMarketplace from "@src/multiCustodian/pages/Advisor/LendingMarketplace";
import InsuranceOfferingDetails from "@src/multiCustodian/pages/Advisor/Marketplace/OfferingDetailsAdvisor/InsuranceOfferingDetailsAdvisor";
import LendingOfferingDetails from "@src/multiCustodian/pages/Advisor/Marketplace/OfferingDetailsAdvisor/LendingOfferingDetailsAdvisor";
import InsuranceOfferingsList from "@src/multiCustodian/pages/Advisor/Marketplace/OfferingsListAdvisor/InsuranceOfferingsListAdvisor";
import LendingOfferingsList from "@src/multiCustodian/pages/Advisor/Marketplace/OfferingsListAdvisor/LendingOfferingsListAdvisor";
import FailedJobs from "@src/multiCustodian/pages/Advisor/MissionControl/Jobs/Failed";
import ReadyJobs from "@src/multiCustodian/pages/Advisor/MissionControl/Jobs/Ready";
import Notifications from "@src/multiCustodian/pages/Advisor/Notifications";
import NotificationDetails from "@src/multiCustodian/pages/Advisor/Notifications/NotificationDetails/NotificationDetails";
import Opportunities from "@src/multiCustodian/pages/Advisor/Opportunities";
import OpportunityDetailsGuard from "@src/multiCustodian/pages/Advisor/Opportunities/Components/OpportunityDetails/OpportunityDetailsGuard";
import Plans from "@src/multiCustodian/pages/Advisor/Plans";
import EditAccount from "@src/multiCustodian/pages/Advisor/Plans/Account";
import EditPlan from "@src/multiCustodian/pages/Advisor/Plans/EditPlan";
import Tasks from "@src/multiCustodian/pages/Advisor/Tasks";
import { TASKS_DEFAULT_QUERY_PARAMS } from "@src/multiCustodian/pages/Advisor/Tasks/common";
import CreateTaskV3 from "@src/multiCustodian/pages/Advisor/Tasks/CreateTask/CreateTaskV3";
import { NavigateTaskDetailsState } from "@src/multiCustodian/pages/Advisor/Tasks/hooks";
import TaskDetailsStandaloneV3 from "@src/multiCustodian/pages/Advisor/Tasks/TaskDetails/TaskDetailsStandaloneV3";
import TaskWorkflows from "@src/multiCustodian/pages/Advisor/TaskWorkflows";
import CreateTaskWorkflow from "@src/multiCustodian/pages/Advisor/TaskWorkflows/CreateTaskWorkflow";
import TaskDetailsWorkflow from "@src/multiCustodian/pages/Advisor/TaskWorkflows/TaskDetailsWorkflow/TaskDetailsWorkflow";
import TaxBudgetAdvisorCreateEdit from "@src/multiCustodian/pages/Advisor/TaxBudgets/CreateEdit";
import TaxBudgetAdvisorCreateHypothetical from "@src/multiCustodian/pages/Advisor/TaxBudgets/CreateHypothetical";
import TransitionWorkflows from "@src/multiCustodian/pages/Advisor/TransitionWorkflows";
import CreateTransitionWorkflow from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/CreateTransitionWorkflow/CreateTransitionWorkflow";
import { useTransitionsNavigation } from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/hooks";
import TransitionsDetailsStandalone from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/TransitionDetails/TransitionsDetailsStandalone";
import TransitionSummary from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/TransitionSummary/TransitionSummary";
import EnableMFA from "@src/multiCustodian/pages/Dashboard/EnableMFA/EnableMFA";
import { State } from "@src/store";
import { YellowstoneShell } from "@src/yellowstone/App";
import * as YellowstoneDashboard from "@src/yellowstone/views/Dashboard";

import PermissionRoute from "./PermissionRoute";
import PrivateAdminRoute from "./PrivateAdminRoute";
import PrivateAdvisorRoute from "./PrivateAdvisorRoute";
import RedirectWithFlag from "./RedirectWithFlag";
import RedirectWithLoading from "./RedirectWithLoading";
import RedirectWithLocation from "./RedirectWithLocation";
import { RouteElement } from "./Router.types";
import {
  AltAssetsMarketplacePath,
  InsuranceMarketplacePath,
  InvestmentsPath,
  LendingMarketplacePath,
  NotificationsPath,
} from "./RouterPaths";
import { NoMatch, RequiresPermission } from "./UtilityComponents";

export const advisorRoutes: RouteElement[] = [
  {
    path: "/Advisor",
    component: PrivateAdvisorRoute,
    noMatch: NoMatch,
    routes: [
      {
        path: "/Advisor",
        component: function RedirectToHomePage(): JSX.Element {
          const { enableAdvisorTransition } = useFlags();
          const {
            shouldRedirectToTransitionLeadershipHub,
            shouldRedirectToTransitionSummary,
            firstTransitionId,
            isLoading,
          } = useTransitionsNavigation();

          if (enableAdvisorTransition) {
            if (shouldRedirectToTransitionLeadershipHub) {
              return (
                <Redirect to={"/Advisor/Transitions"} exact push={false} />
              );
            }

            if (shouldRedirectToTransitionSummary) {
              return (
                <RedirectWithLoading
                  to={
                    firstTransitionId
                      ? `/Advisor/Transitions/${firstTransitionId}/Summary`
                      : // NOTE: if the user has no transitions at all, we redirect to BookAnalytics
                        "/Advisor/BookAnalytics"
                  }
                  isLoading={isLoading}
                />
              );
            }
          }

          // NOTE: if the user has no active transitions, we redirect to BookAnalytics
          return <Redirect to={"/Advisor/BookAnalytics"} exact push={false} />;
        },
      },
      {
        path: "/Advisor/:advisorId/EnableMFA",
        component: EnableMFA,
      },
      {
        path: "/Advisor/Clients",
        component: Clients,
        trackingEvent: { name: "Advisor Open Clients" },
      },
      {
        path: "/Advisor/Clients/Create",
        component: CreateClient,
        trackingEvent: { name: "Advisor Open Create Client" },
      },
      {
        path: "/Advisor/Clients/:clientId",
        component: function RedirectToClientProfile(): JSX.Element {
          const { clientId } = useParams<{
            clientId: ClientId;
          }>();

          return (
            <RedirectWithLocation
              pathname={`/Advisor/Clients/${clientId}/Profile`}
            />
          );
        },
      },
      {
        path: "/Advisor/Clients/:clientId/Profile",
        component: ClientProfile,
        trackingEvent: { name: "Advisor Open Client Profile" },
      },
      {
        path: "/Advisor/Clients/:clientId/Admin",
        component: AdvisorClientAdmin,
        trackingEvent: { name: "Advisor Open Client Admin" },
      },
      {
        path: "/Advisor/Clients/:clientId/Copilot",
        component: AdvisorCopilot,
        trackingEvent: { name: "Advisor Open Client Copilot" },
      },
      {
        path: "/Advisor/Clients/:clientId/Actions",
        component: AdvisorClientActions,
        trackingEvent: { name: "Advisor Open Client Actions" },
      },
      {
        path: "/Advisor/Clients/:clientId/Notes",
        component: function AdvisorClientNotesRedirect(): JSX.Element {
          const { clientId } = useParams<{ clientId: ClientId }>();
          const { enableRelationshipNotes } = useFlags();

          return enableRelationshipNotes ? (
            <AdvisorClientNotes />
          ) : (
            <Redirect
              to={`/Advisor/Clients/${clientId}/Profile`}
              exact
              push={false}
            />
          );
        },
        trackingEvent: { name: "Advisor Open Client Notes" },
      },
      {
        path: "/Advisor/Clients/:clientId/TaxBudgets",
        component: AdvisorClientTaxBudget,
        trackingEvent: { name: "Advisor Open Client Tax-Budgets" },
      },
      {
        path: "/Advisor/Clients/:clientId/TaxBudgets/Create",
        component: TaxBudgetAdvisorCreateEdit,
        trackingEvent: { name: "Advisor Open Create Client Tax-Budget" },
      },
      {
        path: `/Advisor/Clients/:clientId/TaxBudgets/CreateHypothetical`,
        component: function ClientHypotheticalRedirect(): JSX.Element {
          const { clientId } = useParams<{ clientId: ClientId }>();

          const { enableTaxBudgetHypotheticals } = useFlags();

          return enableTaxBudgetHypotheticals ? (
            <TaxBudgetAdvisorCreateHypothetical />
          ) : (
            <Redirect
              to={`/Advisor/Clients/${clientId}/TaxBudgets`}
              exact
              push={false}
            />
          );
        },
        trackingEvent: {
          name: "Advisor Open Create Client Hypothetical-Tax-Budget",
        },
      },
      {
        path: `/Advisor/Clients/:clientId/TaxBudgets/:taxBudgetId`,
        component: function RedirectToSummary(): JSX.Element {
          const { clientId, taxBudgetId } = useParams<{
            clientId: ClientId;
            taxBudgetId: string;
          }>();

          return (
            <Redirect
              to={`/Advisor/Clients/${clientId}/TaxBudgets/${taxBudgetId}/Usage`}
              exact
              push={false}
            />
          );
        },
      },
      {
        path: "/Advisor/Clients/:clientId/TaxBudgets/:taxBudgetId/Edit",
        component: TaxBudgetAdvisorCreateEdit,
        trackingEvent: { name: "Advisor Open Edit Client Tax-Budget" },
      },
      {
        path: "/Advisor/Clients/:clientId/TaxBudgets/:taxBudgetId/*",
        component: TaxBudgetDetails,
        trackingEvent: { name: "Advisor Open Client Tax-Budget" },
      },
      {
        path: "/Advisor/Clients/:clientId/Admin/Account/:accountId/Rename",
        component: AdminRenameAccount,
        trackingEvent: { name: "Advisor Open Admin Rename Client Account" },
      },
      {
        path: "/Advisor/Clients/:clientId/Admin/Account/:accountId/Archive",
        component: AdminArchiveAccount,
        trackingEvent: { name: "Advisor Open Admin Archive Client Account" },
      },
      {
        path: "/Advisor/Clients/:clientId/Admin/Account/:accountId/Delete",
        component: AdminDeleteAccount,
        trackingEvent: { name: "Advisor Open Admin Delete Client Account" },
      },
      // **** Households Routes ****
      // TODO: Add Tracking events for Householding
      {
        path: "/Advisor/Households",
        component: function HouseholdsRedirect(): JSX.Element {
          return <Redirect to="/Advisor/Clients" exact push={false} />;
        },
      },
      {
        path: "/Advisor/Households/:householdId",
        component: function HouseholdDetailsRedirect(): JSX.Element {
          const { householdId } = useParams<{ householdId: string }>();

          return (
            <RedirectWithLocation
              pathname={`/Advisor/Households/${householdId}/Accounts`}
            />
          );
        },
      },
      {
        path: "/Advisor/Households/:householdId/:tab?",
        component: HouseholdsDrawerRouter,
      },
      {
        path: "/Advisor/BookAnalytics",
        component: function RedirectToAdvisorBookAnalytics(): JSX.Element {
          const { advisorId } = useSelector((state: State) => ({
            advisorId: state.main_Reducer.cockroach_advisor_id,
          }));

          if (advisorId === null) {
            return <div>Error finding advisor id</div>;
          }

          return (
            <Redirect
              to={`/Advisor/BookAnalytics/Advisor/${advisorId}`}
              exact
              push={false}
            />
          );
        },
      },
      {
        path: "/Advisor/BookAnalytics/Advisor/:advisorId",
        component: BookAnalytics,
        trackingEvent: { name: "Advisor Open Book Analytics" },
      },
      {
        path: "/Advisor/Tasks",
        component: function AdvisorTasksRedirect({
          children,
        }: React.PropsWithChildren<unknown>) {
          const location = useLocation();
          const ldClient = useLDClient();

          // NOTE: user is anonymous utill we load proper context, think of solution where to move this
          if (ldClient?.getContext().anonymous) {
            return <div />;
          }

          // NOTE: if the user uses a URL with no longer supported task status query param value or the query params are missing, we should resolve the default ones
          if (
            location.search === "" ||
            location.search.includes("AWAITING_RESPONSE")
          ) {
            return (
              <Redirect
                to={{
                  pathname: location.pathname,
                  search: qs.stringify(TASKS_DEFAULT_QUERY_PARAMS, {
                    encode: false,
                    arrayFormat: "bracket-separator",
                  }),
                }}
                exact
                push={false}
              />
            );
          }

          return children;
        } as React.FC,
        routes: [
          {
            path: "/Advisor/Tasks",
            component: function TasksRedirect(): JSX.Element {
              const { enableTaskWorkflows } = useFlags();

              return enableTaskWorkflows ? <TaskWorkflows /> : <Tasks />;
            },
            trackingEvent: { name: "Advisor Open Tasks" },
          },
          {
            path: "/Advisor/Tasks/CreateTask",
            component: function CreateTaskRedirect(): JSX.Element {
              const { enableTaskWorkflows } = useFlags();

              return enableTaskWorkflows ? (
                <CreateTaskWorkflow />
              ) : (
                <CreateTaskV3 />
              );
            },
            trackingEvent: { name: "Advisor Open Create Task" },
          },
          {
            path: "/Advisor/Tasks/CreateSubtask",
            component: function CreateTaskRedirect(): JSX.Element {
              const location = useLocation<TaskDetailsV3 | undefined>();
              const { enableTaskWorkflows } = useFlags();

              if (!location.state) {
                return <Redirect push to="/Advisor/Tasks" />;
              }

              return enableTaskWorkflows ? (
                <CreateTaskWorkflow isSubtaskCreation />
              ) : (
                <CreateTaskV3 isSubtaskCreation />
              );
            },
            trackingEvent: { name: "Advisor Open Create Task" },
          },
          {
            path: "/Advisor/Tasks/:taskId",
            component: function RenderTaskDetailsStandalone(): JSX.Element {
              const location = useLocation<
                NavigateTaskDetailsState | undefined
              >();
              const { enableTaskWorkflows } = useFlags();

              const disableDrawerTransition =
                location.state?.from === "CREATE_SUBTASK";

              return enableTaskWorkflows ? (
                <TaskDetailsWorkflow
                  disableDrawerTransition={disableDrawerTransition}
                />
              ) : (
                <TaskDetailsStandaloneV3
                  disableDrawerTransition={disableDrawerTransition}
                />
              );
            },
            trackingEvent: { name: "Advisor Open Task Details" },
          },
        ],
      },
      {
        path: "/Advisor/Transitions",
        component: function TransitionsRedirect({
          children,
        }: React.PropsWithChildren<unknown>) {
          const ldClient = useLDClient();

          if (ldClient?.getContext().anonymous) {
            return <div />;
          }

          return children;
        } as React.FC,
        routes: [
          {
            path: "/Advisor/Transitions",
            component: function TransitionsRedirect(): JSX.Element {
              const { enableAdvisorTransition } = useFlags();
              const auth = useAdvisorRequestAuth();

              return (
                <RedirectWithFlag
                  flag={enableAdvisorTransition}
                  redirectPath={"/Advisor"}
                >
                  <PermissionRoute
                    permissionCheck={{
                      type: "LeadershipHub",
                      action: "view",
                      id: "Farther",
                    }}
                    redirectPath={"/Advisor"}
                    auth={auth}
                  >
                    <TransitionWorkflows />
                  </PermissionRoute>
                </RedirectWithFlag>
              );
            },
          },
          {
            path: "/Advisor/Transitions/Create",
            component: function CreateTransitionRedirect(): JSX.Element {
              const { enableAdvisorTransition } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableAdvisorTransition}
                  redirectPath={"/Advisor"}
                >
                  <CreateTransitionWorkflow />
                </RedirectWithFlag>
              );
            },
          },
          {
            path: "/Advisor/Transitions/:transitionId",
            component: function TransitionDetailsRedirect(): JSX.Element {
              const { enableAdvisorTransition } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableAdvisorTransition}
                  redirectPath={"/Advisor"}
                >
                  <TransitionsDetailsStandalone />
                </RedirectWithFlag>
              );
            },
          },
          {
            path: "/Advisor/Transitions/:transitionId/Summary",
            component: function TransitionSummaryRedirect(): JSX.Element {
              const { enableAdvisorTransition } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableAdvisorTransition}
                  redirectPath={"/Advisor"}
                >
                  <TransitionSummary />
                </RedirectWithFlag>
              );
            },
          },
        ],
      },
      {
        path: `/Advisor/${NotificationsPath}`,
        component: function NotificationsRedirect({
          children,
        }: React.PropsWithChildren<unknown>): JSX.Element {
          const { enableNotificationsV2 } = useFlags();
          const { advisorId } = useSelector((state: State) => ({
            advisorId: state.main_Reducer.cockroach_advisor_id,
          }));

          return (
            <RedirectWithFlag
              flag={enableNotificationsV2}
              redirectPath={"/Advisor"}
            >
              {advisorId ? (
                <Notifications personId={advisorId}>{children}</Notifications>
              ) : (
                <div />
              )}
            </RedirectWithFlag>
          );
        },
        trackingEvent: { name: "Advisor Open Notifications" },
        routes: [
          {
            path: `/Advisor/${NotificationsPath}/:notificationId`,
            component: function NotificationDetailsRedirect(): JSX.Element {
              const { enableNotificationsV2 } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableNotificationsV2}
                  redirectPath={"/Advisor"}
                >
                  <NotificationDetails />
                </RedirectWithFlag>
              );
            },
            trackingEvent: { name: "Advisor Open Notification Details" },
          },
        ],
      },
      {
        path: `/Advisor/${InvestmentsPath}`,
        component: Investments,
        routes: [
          // **** Root Redirector ****
          {
            path: `/Advisor/${InvestmentsPath}`,
            component: function AdvisorInvestmentsRedirect(): JSX.Element {
              return (
                <Redirect
                  to={`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`}
                  exact
                  push={false}
                />
              );
            },
          },
          // **** Model Marketplace ****
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2`,
            component: PortfolioPlanList,
            trackingEvent: { name: "Advisor Open Model Marketplace" },
          },
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/Create`,
            component: function PortfolioLabCreateRedirect(): JSX.Element {
              const { enablePortfolioLabPhase2 } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enablePortfolioLabPhase2}
                  redirectPath={`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`}
                >
                  <PortfolioLabCreate />
                </RedirectWithFlag>
              );
            },
            trackingEvent: { name: "Advisor Open Create Custom Model" },
          },
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/:portfolioPlanId`,
            trackingEvent: { name: "Advisor Open Model Marketplace" },
            component:
              function AdvisorInvestmentsModelMarketplaceV2Redirect(): JSX.Element {
                const { portfolioPlanId } = useParams<{
                  portfolioPlanId: PortfolioPlanId;
                }>();
                return (
                  <Redirect
                    to={`/Advisor/${InvestmentsPath}/ModelMarketplaceV2/${portfolioPlanId}/ModelDetails`}
                    exact
                    push={false}
                  />
                );
              },
          },
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/:portfolioPlanId/ModelDetails`,
            component: ModelDrawerV2,
            trackingEvent: { name: "Advisor Open Model Details" },
          },
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/:portfolioPlanId/Update`,
            component: function PortfolioLabUpdateRedirect(): JSX.Element {
              const { portfolioPlanId } = useParams<{
                portfolioPlanId: PortfolioPlanId;
              }>();

              const { enablePortfolioLabPhase2 } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enablePortfolioLabPhase2}
                  redirectPath={`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`}
                >
                  <PortfolioLabUpdate portfolioPlanId={portfolioPlanId} />
                </RedirectWithFlag>
              );
            },
          },
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/:portfolioPlanId/Usage`,
            component: ModelDrawerUsageV2,
            trackingEvent: { name: "Advisor Open Model Details" },
          },
          {
            path: `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/:portfolioPlanId/Access`,
            component: AccessTab,
            trackingEvent: { name: "Advisor Open Model Details" },
          },
          // **** Separately-Managed Accounts ****
          {
            path: `/Advisor/${InvestmentsPath}/SMA`,
            component: SMA,
            trackingEvent: { name: "Advisor Open SMA" },
          },
          {
            path: `/Advisor/${InvestmentsPath}/SMA/:SMAId`,
            component: SMADetails,
            trackingEvent: { name: "Advisor Open SMA Details" },
          },
          {
            path: `/Advisor/${InvestmentsPath}/SMA/:SMAId/Apply`,
            component: ApplySMA,
            trackingEvent: { name: "Advisor Open Apply SMA" },
          },
          // **** Alt Assets v2 ****
          {
            path: `/Advisor/${AltAssetsMarketplacePath}`,
            component: AltAssetsMarketplace,
            trackingEvent: { name: "Advisor Open Alternative Asset" },
          },
          {
            path: `/Advisor/${AltAssetsMarketplacePath}/:offeringType`,
            component: AltAssetsOfferingsList,
          },
          {
            path: `/Advisor/${AltAssetsMarketplacePath}/:offeringType/:offeringId`,
            component: AltAssetsOfferingDetails,
          },
        ],
      },
      {
        path: "/Advisor/Opportunities",
        component: Opportunities,
        trackingEvent: { name: "Advisor Open Opportunities" },
      },
      {
        path: "/Advisor/Opportunities/:opportunityId",
        component: OpportunityDetailsGuard,
        trackingEvent: { name: "Advisor Open Opportunity Details" },
      },
      // Any non special pages must come ABOVE here
      {
        path: "/Advisor/Plans",
        component: PrivateAdminRoute,
        noMatch: RequiresPermission,
        routes: [
          {
            path: "/Advisor/Plans",
            component: Plans,
            trackingEvent: { name: "Advisor Open Plans" },
          },
          {
            path: "/Advisor/Plans/:planId",
            component: EditPlan,
            trackingEvent: { name: "Advisor Open Edit Plan" },
          },
          {
            path: "/Advisor/Plans/:planId/Accounts/:accountId",
            component: EditAccount,
            trackingEvent: { name: "Advisor Open Plan Account Details" },
          },
        ],
      },
      {
        path: `/Advisor/${InsuranceMarketplacePath}`,
        component: function InsuranceMarketplaceRedirect(): JSX.Element {
          const { enableInsuranceMarketplace } = useFlags();

          return (
            <RedirectWithFlag
              flag={enableInsuranceMarketplace}
              redirectPath={"/Advisor"}
            >
              <InsuranceMarketplace />
            </RedirectWithFlag>
          );
        },
        trackingEvent: { name: "Advisor Open Insurance Marketplace" },
      },
      {
        path: `/Advisor/${InsuranceMarketplacePath}/:offeringType`,
        component: function OfferingTypeRedirect(): JSX.Element {
          const { enableInsuranceMarketplace } = useFlags();

          return (
            <RedirectWithFlag
              flag={enableInsuranceMarketplace}
              redirectPath={"/Advisor"}
            >
              <InsuranceOfferingsList />
            </RedirectWithFlag>
          );
        },
        trackingEvent: {
          name: "Advisor Open Insurance Marketplace Offering Type",
        },
      },
      {
        path: `/Advisor/${InsuranceMarketplacePath}/:offeringType/:offeringId`,
        component: function OfferingDetailsRedirect(): JSX.Element {
          const { enableInsuranceMarketplace } = useFlags();

          return (
            <RedirectWithFlag
              flag={enableInsuranceMarketplace}
              redirectPath={"/Advisor"}
            >
              <InsuranceOfferingDetails />
            </RedirectWithFlag>
          );
        },
        trackingEvent: {
          name: "Advisor Open Insurance Marketplace Offering Details",
        },
      },
      {
        path: `/Advisor/${LendingMarketplacePath}`,
        component: function LendingMarketplaceRedirect(): JSX.Element {
          const { enableLendingMarketplace } = useFlags();

          return (
            <RedirectWithFlag
              flag={enableLendingMarketplace}
              redirectPath={"/Advisor"}
            >
              <LendingMarketplace />
            </RedirectWithFlag>
          );
        },
        trackingEvent: { name: "Advisor Open Lending Marketplace" },
      },
      {
        path: `/Advisor/${LendingMarketplacePath}/:offeringType`,
        component: function OfferingTypeRedirect(): JSX.Element {
          const { enableLendingMarketplace } = useFlags();

          return (
            <RedirectWithFlag
              flag={enableLendingMarketplace}
              redirectPath={"/Advisor"}
            >
              <LendingOfferingsList />
            </RedirectWithFlag>
          );
        },
        trackingEvent: {
          name: "Advisor Open Lending Marketplace Offering Type",
        },
      },
      {
        path: `/Advisor/${LendingMarketplacePath}/:offeringType/:offeringId`,
        component: function OfferingDetailsRedirect(): JSX.Element {
          const { enableLendingMarketplace } = useFlags();

          return (
            <RedirectWithFlag
              flag={enableLendingMarketplace}
              redirectPath={"/Advisor"}
            >
              <LendingOfferingDetails />
            </RedirectWithFlag>
          );
        },
        trackingEvent: {
          name: "Advisor Open Lending Marketplace Offering Details",
        },
      },
      {
        path: "/Advisor/FinancialOperations",
        component: PrivateAdminRoute,
        noMatch: RequiresPermission,
        routes: [
          {
            path: "/Advisor/FinancialOperations",
            component: function Redirecting(): JSX.Element {
              return (
                <Redirect
                  to={`/Advisor/FinancialOperations/TradeProposals`}
                  exact
                  push={false}
                />
              );
            },
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/TradeProposals",
            component: TradeProposals,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/TradeProposals/:proposalId",
            component: TradeProposal,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/TradeProposalBundles",
            component: TradeProposalBundles,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/TradeProposalBundles/:bundleId",
            component: TradeProposalBundle,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/TradeProposalBundles/:bundleId/:proposalId",
            component: TradeProposalBundleProposalDetail,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/StagedOrders",
            component: StagedOrders,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/Itbs",
            component: Itbs,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/ItbPacks",
            component: ItbPacks,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/FinancialOperations/Rebalances",
            component: Rebalances,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
        ],
      },
      {
        path: "/Advisor/MissionControl",
        component: PrivateAdminRoute,
        noMatch: RequiresPermission,
        routes: [
          {
            path: "/Advisor/MissionControl",
            component: function Redirecting(): JSX.Element {
              return (
                <Redirect
                  to={`/Advisor/MissionControl/Jobs/Failed`}
                  exact
                  push={false}
                />
              );
            },
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/MissionControl/Jobs/Failed",
            component: FailedJobs,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
          {
            path: "/Advisor/MissionControl/Jobs/Ready",
            component: ReadyJobs,
            trackingEvent: { name: "Advisor Click Client-Ops" },
          },
        ],
      },
      // Yellowstone routes
      {
        path: "/Advisor/Client-Ops",
        component: PrivateAdminRoute,
        noMatch: NoMatch,
        routes: [
          {
            path: "/Advisor/Client-Ops",
            component: YellowstoneShell,
            noMatch: NoMatch,
            routes: [
              {
                path: "/Advisor/Client-Ops",
                component: () => (
                  <Redirect
                    to="/Advisor/Client-Ops/Dashboard"
                    exact
                    push={false}
                  />
                ),
              },
              {
                path: "/Advisor/Client-Ops/Dashboard",
                component: YellowstoneDashboard.Dashboard,
                noMatch: NoMatch,
                routes: [
                  {
                    path: "/Advisor/Client-Ops/Dashboard",
                    component: () => (
                      <Redirect
                        to="/Advisor/Client-Ops/Dashboard/Daily-Trading"
                        exact
                        push={false}
                      />
                    ),
                  },
                  // Accounts
                  {
                    path: "/Advisor/Client-Ops/Dashboard/Accounts",
                    component: () => (
                      <YellowstoneDashboard.DashboardAccounts
                        isSelectedUserMode={false}
                      />
                    ),
                  },
                  {
                    path: "/Advisor/Client-Ops/Dashboard/Accounts/:userId",
                    component: () => (
                      <YellowstoneDashboard.DashboardAccounts
                        isSelectedUserMode={true}
                      />
                    ),
                  },
                  // Daily-Trading
                  {
                    path: "/Advisor/Client-Ops/Dashboard/Daily-Trading",
                    component: YellowstoneDashboard.DashboardDailyTrading,
                    noMatch: NoMatch,
                    routes: [
                      {
                        path: "/Advisor/Client-Ops/Dashboard/Daily-Trading",
                        component: () => (
                          <Redirect
                            to="/Advisor/Client-Ops/Dashboard/Daily-Trading/Daily-Proposals"
                            exact
                            push={false}
                          />
                        ),
                      },
                      // PQS proposals
                      {
                        path: "/Advisor/Client-Ops/Dashboard/Daily-Trading/Daily-Proposals",
                        component: () => (
                          <YellowstoneDashboard.ProposalsController
                            isDailyProposalsView={true}
                          />
                        ),
                      },
                      {
                        path: "/Advisor/Client-Ops/Dashboard/Daily-Trading/Submitted-Proposals",
                        component: () => (
                          <YellowstoneDashboard.ProposalsController
                            isSubmittedProposalsView={true}
                          />
                        ),
                      },
                      {
                        path: "/Advisor/Client-Ops/Dashboard/Daily-Trading/Skipped-Proposals",
                        component: () => (
                          <YellowstoneDashboard.ProposalsController
                            isSkippedProposalsView={true}
                          />
                        ),
                      },
                      {
                        path: "/Advisor/Client-Ops/Dashboard/Daily-Trading/Proposal-Details/:proposalId",
                        component: () => (
                          <YellowstoneDashboard.ProposalDetailsGuard />
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "/Advisor/Admin",
        component: PrivateAdminRoute,
        noMatch: RequiresPermission,
        routes: [
          {
            path: "/Advisor/Admin",
            component: () => <Admin />,
            trackingEvent: { name: "Advisor Open Admin" },
          },
          {
            path: "/Advisor/Admin/CreateCXMember",
            component: CreateCXMember,
            trackingEvent: { name: "Advisor Open Admin Create CX Member" },
          },
          {
            path: "/Advisor/Admin/CreateBulkClients",
            component: CreateBulkClients,
            trackingEvent: { name: "Advisor Open Admin Create Bulk Clients" },
          },
          {
            path: "/Advisor/Admin/CreateAdvisor",
            component: function CreateAdvisorV2Redirect(): JSX.Element {
              const { enableCreateAdvisorV2 } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableCreateAdvisorV2}
                  redirectPath={"/Advisor/Admin"}
                >
                  <CreateAdvisor />
                </RedirectWithFlag>
              );
            },
            trackingEvent: {
              name: "Advisor Open Admin Create Advisor",
            },
          },
          {
            path: "/Advisor/Admin/AllPortfoliosBilling",
            component: AllPortfoliosBilling,
            trackingEvent: {
              name: "Advisor Open Admin All Portfolios Billing",
            },
          },
          {
            path: "/Advisor/Admin/CustomModelBilling",
            component: CustomModelBilling,
            trackingEvent: { name: "Advisor Open Admin Custom Model Billing" },
          },
          {
            path: "/Advisor/Admin/FAMBilling",
            component: FAMBilling,
            trackingEvent: { name: "Advisor Open Admin FAM Billing" },
          },
          {
            path: "/Advisor/Admin/EquivalentSecurities/Create",
            component: function EquivalentSecuritiesRedirect(): JSX.Element {
              const { enableEquivalentSecurities } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableEquivalentSecurities}
                  redirectPath={"/Advisor/Admin"}
                >
                  <CreateEquivalentSecurities />
                </RedirectWithFlag>
              );
            },
            trackingEvent: {
              name: "Advisor Open Create Equivalent Securities List",
            },
          },
          {
            path: "/Advisor/Admin/EquivalentSecurities/:listId/Edit",
            component: function EquivalentSecuritiesRedirect(): JSX.Element {
              const { enableEquivalentSecurities } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableEquivalentSecurities}
                  redirectPath={"/Advisor/Admin"}
                >
                  <EditEquivalentSecurities />
                </RedirectWithFlag>
              );
            },
            trackingEvent: {
              name: "Advisor Open Edit Equivalent Securities List",
            },
          },
          {
            path: "/Advisor/Admin/EquivalentSecurities/:listId/Delete",
            component: function EquivalentSecuritiesRedirect(): JSX.Element {
              const { enableEquivalentSecurities } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableEquivalentSecurities}
                  redirectPath={"/Advisor/Admin"}
                >
                  <DeleteEquivalentSecurities />
                </RedirectWithFlag>
              );
            },
            trackingEvent: {
              name: "Advisor Open Delete Equivalent Securities List",
            },
          },
          {
            path: "/Advisor/Admin/EquivalentSecurities/:listId",
            component: function EquivalentSecuritiesRedirect(): JSX.Element {
              const { enableEquivalentSecurities } = useFlags();

              return (
                <RedirectWithFlag
                  flag={enableEquivalentSecurities}
                  redirectPath={"/Advisor/Admin"}
                >
                  <EquivalentSecuritiesDrawer />
                </RedirectWithFlag>
              );
            },
            trackingEvent: { name: "Advisor Open Equivalent Securities List" },
          },
          {
            path: "/Advisor/Admin/EquivalentSecurities",
            component: () => (
              <Redirect to={"/Advisor/Admin"} exact push={false} />
            ),
          },
          {
            path: "/Advisor/Admin/Disclosures",
            component: DisclosureAgreements,
            trackingEvent: {
              name: "Advisor Open Disclosures",
            },
          },
        ],
      },
    ],
  },
];
